import { cloneElement, type CSSProperties, type ReactElement } from 'react';
import { Link } from 'react-router';

import { cn } from '@/components/ui/cn';
import { useBackState } from '@/lib/back-nav';

import { formatCustomValue } from '@/components/documents/custom-field-format';

import { formatApproval, formatFrequency, formatRelated } from '../column-format';
import { DEFAULT_VISIBLE, visibleDefs, type ColumnId } from '../columns';
import { reviewFlag } from '../document-status';
import { formatReviewDate, formatVersion } from '../review-date';
import type { CustomFieldColumn, MasterListRow } from '../types';
import { PersonChip } from './PersonChip';
import { StatusBadge } from './StatusBadge';
import { formatDate } from '@/lib/time';

const DANGER: CSSProperties = { color: 'var(--danger)', fontWeight: 700 };

type Cell = (row: MasterListRow, now: Date) => ReactElement<{ 'data-label'?: string }>;

// Contenido de cada columna (la del código es el enlace y se arma aparte).
const CELLS: Record<Exclude<ColumnId, 'code'>, Cell> = {
  title: (r) => <td>{r.title ?? '—'}</td>,
  type: (r) => <td>{r.documentType.name}</td>,
  process: (r) => <td>{r.processArea.name}</td>,
  version: (r) => <td className="dim-mono">{formatVersion(r.version, r.status)}</td>,
  status: (r) => (
    <td>
      <StatusBadge status={r.status} nextReviewAt={r.nextReviewAt} inTraining={r.inTraining} />
    </td>
  ),
  norms: (r) => (
    <td className="dim-mono">
      {Array.from(new Set(r.norms.map((n) => n.normCode))).join(' · ') || '—'}
    </td>
  ),
  responsible: (r) => (
    <td>
      <PersonChip person={r.responsible} />
    </td>
  ),
  owner: (r) => (
    <td>
      <PersonChip person={r.owner} />
    </td>
  ),
  approval: (r) => <td className="dim-mono">{formatApproval(r.approvedAt)}</td>,
  effective: (r) => <td className="dim-mono">{formatDate(r.effectiveDate)}</td>,
  frequency: (r) => <td className="dim-mono">{formatFrequency(r.reviewFrequencyDays)}</td>,
  nextReview: (r, now) => {
    const rev = formatReviewDate(r.nextReviewAt, now);
    return (
      <td
        className={cn(rev.tone !== 'danger' && 'dim-mono')}
        style={rev.tone === 'danger' ? DANGER : undefined}
      >
        {rev.text}
      </td>
    );
  },
  related: (r) => <td className="dim-mono">{formatRelated(r.related)}</td>,
};

export function DocumentTable({
  rows,
  columns = DEFAULT_VISIBLE,
  customColumns = [],
}: {
  rows: MasterListRow[];
  /** Columnas a mostrar (selector "Columnas"); por defecto, las del mockup. */
  columns?: readonly ColumnId[];
  /** Campos propios elegidos de la empresa activa (QA F1 #14), antes de la acción. */
  customColumns?: CustomFieldColumn[];
}): ReactElement {
  const fromHere = useBackState('Lista Maestra');
  const now = new Date();
  const defs = visibleDefs(columns);
  return (
    // G4 (QA UX-03): en móvil se lee como tarjetas (`.t-cards`), cada celda con su etiqueta.
    <table className="t t-cards">
      <caption className="sr-only">Lista maestra de documentos de la empresa activa</caption>
      <thead>
        <tr>
          {defs.map((c) => (
            <th key={c.id} scope="col" className={cn(c.id === 'code' && 't-pin-start')}>
              {c.label}
            </th>
          ))}
          {customColumns.map((c) => (
            <th key={`cf-${c.key}`} scope="col">
              {c.label}
            </th>
          ))}
          <th scope="col" className="t-pin-end">
            <span className="sr-only">Acciones</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const vencido =
            row.status === 'vigente' && reviewFlag(row.nextReviewAt, now) === 'vencido';
          return (
            <tr key={row.id} className={cn(vencido && 'alert')}>
              {defs.map((c) =>
                c.id === 'code' ? (
                  // QA Julián 2026-10-13: el código abre la ficha y queda fijo a la
                  // izquierda, así se abre un documento sin desplazar la tabla.
                  <td key="code" className="t-pin-start" data-label={c.label}>
                    <Link
                      to={`/documentos/${row.id}`}
                      state={fromHere}
                      className="code t-code-link"
                    >
                      {row.code}
                    </Link>
                  </td>
                ) : (
                  cloneElement(CELLS[c.id](row, now), { key: c.id, 'data-label': c.label })
                ),
              )}
              {customColumns.map((c) => {
                const text = formatCustomValue(c.dataType, row.customFields[c.key]);
                return (
                  <td key={`cf-${c.key}`} data-label={c.label}>
                    {/* Un valor largo se recorta; el completo queda en el título. */}
                    <span className="cf-cell" title={text === '—' ? undefined : text}>
                      {text}
                    </span>
                  </td>
                );
              })}
              <td className="t-pin-end t-act" data-label="Acciones">
                <Link
                  to={`/documentos/${row.id}`}
                  state={fromHere}
                  className="btn ghost sm"
                  aria-label={`Abrir el expediente de ${row.code}`}
                >
                  Abrir
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
